// gd-step-carousel.js — horizontal scroll-snap step carousel with active-step description.
// Matches Bolt ui/StepsCarousel.tsx (inside ApplicationStepsPanel + RemovalStepsPanel).
// Two modes:
//   - Single-flow: slides are rendered server-side by the snippet, JS just wires interaction.
//   - Variant-mode: root has [data-gd-step-variant-mode]. Chip row gets built from JSON; slides
//     are rebuilt on chip click from the active variant's data.
(() => {
  'use strict';
  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function buildSlides(track, steps, assetBase) {
    track.innerHTML = '';
    steps.titles.forEach((title, i) => {
      const slide = document.createElement('div');
      slide.className = 'gd-step-carousel__slide';
      slide.dataset.gdStepIdx = String(i);
      const media = document.createElement('div');
      media.className = 'gd-step-carousel__media';
      const imgName = steps.images?.[i];
      if (imgName) {
        const img = document.createElement('img');
        img.src = assetBase + imgName;
        img.alt = title;
        img.loading = 'lazy';
        img.className = 'gd-step-carousel__img';
        media.appendChild(img);
      }
      const tip = steps.tips?.[i];
      if (tip) {
        const pill = document.createElement('span');
        pill.className = 'gd-step-carousel__tip-pill';
        pill.textContent = 'Tip: ' + tip;
        media.appendChild(pill);
      }
      slide.appendChild(media);
      track.appendChild(slide);
    });
  }

  function init(root) {
    if (root.dataset.gdStepInit === '1') return;
    root.dataset.gdStepInit = '1';

    const track = root.querySelector('[data-gd-step-track]');
    if (!track) return;

    const prev = root.querySelector('[data-gd-step-prev]');
    const next = root.querySelector('[data-gd-step-next]');
    const dotsHost = root.querySelector('[data-gd-step-dots]');
    const titleEl = root.querySelector('[data-gd-step-title]');
    const textEl = root.querySelector('[data-gd-step-text]');
    const dataEl = root.querySelector('[data-gd-step-data]');
    const variantHost = root.querySelector('[data-gd-step-variants]');
    const variantTip = root.querySelector('[data-gd-step-variant-tip]');
    const variantTipText = root.querySelector('[data-gd-step-variant-tip-text]');
    const isVariantMode = root.hasAttribute('data-gd-step-variant-mode');

    let data = { variants: [] };
    try { data = JSON.parse(dataEl?.textContent || '{}'); } catch (_) { /* ignore */ }
    if (!data.variants || !data.variants.length) return;

    // Asset URL base: use the first already-rendered <img> to infer the Shopify CDN prefix.
    // If none was server-rendered (variant mode), fall back to a relative path; the script tag
    // stores raw filenames, and we rebuild slides via /cdn/shop/files/<filename>. For the dev
    // server and in-theme paths, the asset_url equivalent is the theme's /assets/<filename>.
    const firstImg = track.querySelector('img');
    let assetBase = '/assets/';
    if (firstImg) {
      const src = firstImg.getAttribute('src') || '';
      const slash = src.lastIndexOf('/');
      if (slash >= 0) assetBase = src.slice(0, slash + 1);
    }

    let activeVariant = 0;
    let active = 0;

    function cardStep() {
      const first = track.firstElementChild;
      if (!first) return 0;
      const w = first.offsetWidth || 0;
      const cs = getComputedStyle(track);
      const gap = parseFloat(cs.gap) || 0;
      return w + gap;
    }

    function currentSteps() {
      return data.variants[activeVariant];
    }

    function rebuildDots() {
      if (!dotsHost) return;
      const count = currentSteps()?.titles?.length || 0;
      dotsHost.innerHTML = '';
      for (let i = 0; i < count; i++) {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'gd-step-carousel__dot';
        d.setAttribute('aria-label', 'Go to step ' + (i + 1));
        d.addEventListener('click', () => goTo(i));
        dotsHost.appendChild(d);
      }
    }

    function render() {
      const steps = currentSteps();
      const count = steps?.titles?.length || 0;
      if (dotsHost) [...dotsHost.children].forEach((d, i) => d.classList.toggle('is-active', i === active));
      if (titleEl) titleEl.textContent = count ? `Step ${active + 1}: ${steps.titles[active] || ''}` : '';
      if (textEl)  textEl.textContent  = count ? (steps.texts?.[active] || '') : '';
      if (prev) prev.hidden = active <= 0;
      if (next) next.hidden = active >= count - 1;
      if (variantTip && variantTipText) {
        const msg = steps?.tip;
        if (msg) { variantTipText.textContent = msg; variantTip.hidden = false; }
        else variantTip.hidden = true;
      }
    }

    function goTo(idx) {
      const count = currentSteps()?.titles?.length || 0;
      active = Math.max(0, Math.min(idx, count - 1));
      render();
      const step = cardStep();
      if (step > 0) track.scrollTo({ left: active * step, behavior: prefersReduced ? 'auto' : 'smooth' });
    }

    function switchVariant(idx) {
      if (idx < 0 || idx >= data.variants.length) return;
      activeVariant = idx;
      active = 0;
      buildSlides(track, currentSteps(), assetBase);
      rebuildDots();
      track.scrollLeft = 0;
      if (variantHost) [...variantHost.children].forEach((c, i) => c.classList.toggle('is-active', i === idx));
      render();
    }

    // Variant mode: build chip row, hydrate slides for variant 0
    if (isVariantMode && variantHost) {
      variantHost.innerHTML = '';
      data.variants.forEach((v, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'gd-step-carousel__variant-chip' + (i === 0 ? ' is-active' : '');
        btn.dataset.gdVariantIdx = String(i);
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.textContent = (v.label || '').toUpperCase();
        btn.addEventListener('click', () => {
          switchVariant(i);
          [...variantHost.children].forEach((c, j) => c.setAttribute('aria-selected', j === i ? 'true' : 'false'));
        });
        variantHost.appendChild(btn);
      });
      buildSlides(track, currentSteps(), assetBase);
    }

    rebuildDots();

    track.addEventListener('scroll', () => {
      const step = cardStep();
      if (step <= 0) return;
      const idx = Math.round(track.scrollLeft / step);
      const count = currentSteps()?.titles?.length || 0;
      if (idx !== active && idx >= 0 && idx < count) { active = idx; render(); }
    }, { passive: true });

    prev?.addEventListener('click', () => goTo(active - 1));
    next?.addEventListener('click', () => goTo(active + 1));

    render();
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-gd-step-carousel]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  // Re-init when an accordion containing the carousel opens (layout wasn't measurable before)
  document.addEventListener('toggle', (e) => {
    if (e.target && e.target.matches && e.target.matches('details')) initAll(e.target);
  }, true);
})();
